import { lazy, Suspense, useEffect, useRef, useState, useMemo } from "react";
import type { GlobeMethods } from "react-globe.gl";
import earthDayImage from "@/assets/images/earth/earth-day.webp";
import earthTopologyImage from "@/assets/images/earth/earth-topology.png";
import nightSkyImage from "@/assets/images/earth/night-sky.png";
// Preload immediately
const globePromise = import("react-globe.gl");
const Globe = lazy(() => globePromise);

// Pre-computed color strings for opacity values (0.00 to 1.00 in 0.01 steps)
const OPACITY_COLOR_CACHE: string[] = [];
for (let i = 0; i <= 100; i++) {
	const opacity = i / 100;
	OPACITY_COLOR_CACHE[i] = `rgba(255, 255, 255, ${opacity.toFixed(2)})`;
}

// Get cached color string for an opacity value
function getArcColor(opacity: number): string {
	const index = Math.round(Math.max(0, Math.min(1, opacity)) * 100);
	return OPACITY_COLOR_CACHE[index];
}

// Fisher-Yates shuffle - O(n) and unbiased
function shuffleArray<T>(array: T[]): T[] {
	const result = [...array];
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
}

type SceneChild = {
	isLight?: boolean;
};

type SceneLike = {
	children: SceneChild[];
	remove: (object: SceneChild) => void;
	add: (object: unknown) => void;
};

type GlobeRenderer = {
	toneMapping?: number;
	toneMappingExposure?: number;
	outputColorSpace?: number;
};

type GlobeControls = {
	autoRotate: boolean;
	autoRotateSpeed: number;
	enableZoom: boolean;
	enablePan: boolean;
	enableRotate: boolean;
	update: () => void;
};

type DirectionalLight = {
	position: { set: (x: number, y: number, z: number) => void };
};

type ThreeLike = {
	DirectionalLight: new (color: number, intensity: number) => DirectionalLight;
	AmbientLight: new (color: number, intensity: number) => unknown;
	ACESFilmicToneMapping: number;
	SRGBColorSpace: number;
};

// City with activity weight (1-5, higher = more active)
interface City {
	name: string;
	lat: number;
	lng: number;
	weight: number;
}

// 120+ cities worldwide with activity weights
const CITIES: City[] = [
	// North America - Major
	{ name: "New York", lat: 40.7128, lng: -74.006, weight: 7 },
	{ name: "Los Angeles", lat: 34.0522, lng: -118.2437, weight: 7 },
	{ name: "San Francisco", lat: 37.7749, lng: -122.4194, weight: 7 },
	{ name: "Chicago", lat: 41.8781, lng: -87.6298, weight: 4 },
	{ name: "Toronto", lat: 43.6532, lng: -79.3832, weight: 4 },
	{ name: "Miami", lat: 25.7617, lng: -80.1918, weight: 3 },
	{ name: "Seattle", lat: 47.6062, lng: -122.3321, weight: 4 },
	{ name: "Austin", lat: 30.2672, lng: -97.7431, weight: 3 },
	{ name: "Denver", lat: 39.7392, lng: -104.9903, weight: 3 },
	{ name: "Vancouver", lat: 49.2827, lng: -123.1207, weight: 3 },
	{ name: "Boston", lat: 42.3601, lng: -71.0589, weight: 4 },
	{ name: "Atlanta", lat: 33.749, lng: -84.388, weight: 3 },
	{ name: "Montreal", lat: 45.5017, lng: -73.5673, weight: 3 },
	{ name: "Dallas", lat: 32.7767, lng: -96.797, weight: 3 },
	{ name: "Phoenix", lat: 33.4484, lng: -112.074, weight: 2 },
	{ name: "Philadelphia", lat: 39.9526, lng: -75.1652, weight: 3 },
	{ name: "Houston", lat: 29.7604, lng: -95.3698, weight: 3 },
	{ name: "Washington DC", lat: 38.9072, lng: -77.0369, weight: 4 },
	// Latin America
	{ name: "Mexico City", lat: 19.4326, lng: -99.1332, weight: 4 },
	{ name: "São Paulo", lat: -23.5505, lng: -46.6333, weight: 5 },
	{ name: "Buenos Aires", lat: -34.6037, lng: -58.3816, weight: 4 },
	{ name: "Santiago", lat: -33.4489, lng: -70.6693, weight: 3 },
	{ name: "Bogotá", lat: 4.711, lng: -74.0721, weight: 3 },
	{ name: "Lima", lat: -12.0464, lng: -77.0428, weight: 3 },
	{ name: "Medellín", lat: 6.2442, lng: -75.5812, weight: 2 },
	{ name: "Rio de Janeiro", lat: -22.9068, lng: -43.1729, weight: 3 },
	{ name: "Guadalajara", lat: 20.6597, lng: -103.3496, weight: 2 },
	{ name: "Montevideo", lat: -34.9011, lng: -56.1645, weight: 2 },
	{ name: "Caracas", lat: 10.4806, lng: -66.9036, weight: 2 },
	{ name: "Quito", lat: -0.1807, lng: -78.4678, weight: 2 },
	// Europe - Major
	{ name: "London", lat: 51.5074, lng: -0.1278, weight: 4 },
	{ name: "Paris", lat: 48.8566, lng: 2.3522, weight: 3 },
	{ name: "Berlin", lat: 52.52, lng: 13.405, weight: 3 },
	{ name: "Amsterdam", lat: 52.3676, lng: 4.9041, weight: 2 },
	{ name: "Stockholm", lat: 59.3293, lng: 18.0686, weight: 1 },
	{ name: "Zurich", lat: 47.3769, lng: 8.5417, weight: 2 },
	{ name: "Dublin", lat: 53.3498, lng: -6.2603, weight: 2 },
	{ name: "Lisbon", lat: 38.7223, lng: -9.1393, weight: 1 },
	{ name: "Barcelona", lat: 41.3851, lng: 2.1734, weight: 2 },
	{ name: "Milan", lat: 45.4642, lng: 9.19, weight: 2 },
	{ name: "Munich", lat: 48.1351, lng: 11.582, weight: 2 },
	{ name: "Vienna", lat: 48.2082, lng: 16.3738, weight: 1 },
	{ name: "Copenhagen", lat: 55.6761, lng: 12.5683, weight: 1 },
	{ name: "Helsinki", lat: 60.1699, lng: 24.9384, weight: 1 },
	{ name: "Prague", lat: 50.0755, lng: 14.4378, weight: 1 },
	{ name: "Warsaw", lat: 52.2297, lng: 21.0122, weight: 1 },
	{ name: "Madrid", lat: 40.4168, lng: -3.7038, weight: 2 },
	{ name: "Rome", lat: 41.9028, lng: 12.4964, weight: 2 },
	{ name: "Brussels", lat: 50.8503, lng: 4.3517, weight: 1 },
	{ name: "Oslo", lat: 59.9139, lng: 10.7522, weight: 1 },
	{ name: "Budapest", lat: 47.4979, lng: 19.0402, weight: 1 },
	{ name: "Bucharest", lat: 44.4268, lng: 26.1025, weight: 1 },
	{ name: "Athens", lat: 37.9838, lng: 23.7275, weight: 1 },
	{ name: "Kiev", lat: 50.4501, lng: 30.5234, weight: 1 },
	{ name: "Moscow", lat: 55.7558, lng: 37.6173, weight: 2 },
	{ name: "St Petersburg", lat: 59.9311, lng: 30.3609, weight: 1 },
	{ name: "Geneva", lat: 46.2044, lng: 6.1432, weight: 1 },
	{ name: "Frankfurt", lat: 50.1109, lng: 8.6821, weight: 2 },
	// Asia - Major
	{ name: "Tokyo", lat: 35.6762, lng: 139.6503, weight: 3 },
	{ name: "Singapore", lat: 1.3521, lng: 103.8198, weight: 3 },
	{ name: "Hong Kong", lat: 22.3193, lng: 114.1694, weight: 2 },
	{ name: "Seoul", lat: 37.5665, lng: 126.978, weight: 3 },
	{ name: "Shanghai", lat: 31.2304, lng: 121.4737, weight: 2 },
	{ name: "Beijing", lat: 39.9042, lng: 116.4074, weight: 2 },
	{ name: "Mumbai", lat: 19.076, lng: 72.8777, weight: 2 },
	{ name: "Bangalore", lat: 12.9716, lng: 77.5946, weight: 2 },
	{ name: "Bangkok", lat: 13.7563, lng: 100.5018, weight: 2 },
	{ name: "Jakarta", lat: -6.2088, lng: 106.8456, weight: 2 },
	{ name: "Taipei", lat: 25.033, lng: 121.5654, weight: 2 },
	{ name: "Dubai", lat: 25.2048, lng: 55.2708, weight: 2 },
	{ name: "Tel Aviv", lat: 32.0853, lng: 34.7818, weight: 2 },
	{ name: "Osaka", lat: 34.6937, lng: 135.5023, weight: 2 },
	{ name: "Shenzhen", lat: 22.5431, lng: 114.0579, weight: 1 },
	{ name: "Delhi", lat: 28.6139, lng: 77.209, weight: 2 },
	{ name: "Kuala Lumpur", lat: 3.139, lng: 101.6869, weight: 1 },
	{ name: "Ho Chi Minh", lat: 10.8231, lng: 106.6297, weight: 1 },
	{ name: "Manila", lat: 14.5995, lng: 120.9842, weight: 1 },
	{ name: "Hanoi", lat: 21.0285, lng: 105.8542, weight: 1 },
	{ name: "Hyderabad", lat: 17.385, lng: 78.4867, weight: 1 },
	{ name: "Chennai", lat: 13.0827, lng: 80.2707, weight: 1 },
	{ name: "Pune", lat: 18.5204, lng: 73.8567, weight: 1 },
	{ name: "Kolkata", lat: 22.5726, lng: 88.3639, weight: 1 },
	{ name: "Guangzhou", lat: 23.1291, lng: 113.2644, weight: 1 },
	{ name: "Chengdu", lat: 30.5728, lng: 104.0668, weight: 1 },
	{ name: "Hangzhou", lat: 30.2741, lng: 120.1551, weight: 1 },
	{ name: "Nanjing", lat: 32.0603, lng: 118.7969, weight: 1 },
	{ name: "Wuhan", lat: 30.5928, lng: 114.3055, weight: 1 },
	{ name: "Xian", lat: 34.3416, lng: 108.9398, weight: 1 },
	{ name: "Busan", lat: 35.1796, lng: 129.0756, weight: 2 },
	{ name: "Nagoya", lat: 35.1815, lng: 136.9066, weight: 2 },
	{ name: "Fukuoka", lat: 33.5904, lng: 130.4017, weight: 1 },
	{ name: "Kyoto", lat: 35.0116, lng: 135.7681, weight: 1 },
	// Middle East
	{ name: "Abu Dhabi", lat: 24.4539, lng: 54.3773, weight: 2 },
	{ name: "Riyadh", lat: 24.7136, lng: 46.6753, weight: 2 },
	{ name: "Doha", lat: 25.2854, lng: 51.531, weight: 2 },
	{ name: "Kuwait City", lat: 29.3759, lng: 47.9774, weight: 1 },
	{ name: "Amman", lat: 31.9454, lng: 35.9284, weight: 1 },
	{ name: "Beirut", lat: 33.8938, lng: 35.5018, weight: 1 },
	// Pacific Islands
	{ name: "Honolulu", lat: 21.3069, lng: -157.8583, weight: 4 },
	// Oceania
	{ name: "Sydney", lat: -33.8688, lng: 151.2093, weight: 5 },
	{ name: "Melbourne", lat: -37.8136, lng: 144.9631, weight: 4 },
	{ name: "Auckland", lat: -36.8485, lng: 174.7633, weight: 2 },
	{ name: "Brisbane", lat: -27.4698, lng: 153.0251, weight: 2 },
	{ name: "Perth", lat: -31.9505, lng: 115.8605, weight: 1 },
	{ name: "Wellington", lat: -41.2865, lng: 174.7762, weight: 1 },
	// Africa
	{ name: "Cape Town", lat: -33.9249, lng: 18.4241, weight: 3 },
	{ name: "Lagos", lat: 6.5244, lng: 3.3792, weight: 4 },
	{ name: "Nairobi", lat: -1.2921, lng: 36.8219, weight: 3 },
	{ name: "Cairo", lat: 30.0444, lng: 31.2357, weight: 4 },
	{ name: "Johannesburg", lat: -26.2041, lng: 28.0473, weight: 3 },
	{ name: "Casablanca", lat: 33.5731, lng: -7.5898, weight: 2 },
	{ name: "Accra", lat: 5.6037, lng: -0.187, weight: 2 },
	{ name: "Addis Ababa", lat: 8.9806, lng: 38.7578, weight: 2 },
	{ name: "Dar es Salaam", lat: -6.7924, lng: 39.2083, weight: 2 },
	{ name: "Kigali", lat: -1.9403, lng: 29.8739, weight: 2 },
	{ name: "Tunis", lat: 36.8065, lng: 10.1815, weight: 2 },
	{ name: "Algiers", lat: 36.7538, lng: 3.0588, weight: 2 },
	{ name: "Dakar", lat: 14.7167, lng: -17.4677, weight: 2 },
	{ name: "Abuja", lat: 9.0765, lng: 7.3986, weight: 2 },
];

interface Arc {
	id: string;
	startLat: number;
	startLng: number;
	endLat: number;
	endLng: number;
	arcAlt: number;
	baseOpacity: number; // Each arc's resting opacity (0.02-0.10)
	opacity: number;
	weight: number; // Based on connected cities' weights (for pulse frequency)
}

interface PulseState {
	startTime: number;
	phase: "up" | "down";
}

// Easing functions for smooth pulse animation
const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

// Generate base opacity - randomized across 1-4% range with weight influence
const randomBaseOpacity = (weight: number) => {
	// Base random across full range
	const baseRandom = 0.01 + Math.random() * 0.03; // 1-4%
	// Weight adds slight bias toward higher opacity for major cities
	const weightBonus = weight >= 5 ? Math.random() * 0.01 : 0; // up to +1% for major
	return Math.min(baseRandom + weightBonus, 0.05);
};

const PEAK_OPACITY = 1.0;
const PULSE_UP_DURATION = 200; // ms
const PULSE_DOWN_DURATION = 4000; // ms

// Connection counts per city weight
const CONNECTION_COUNTS: Record<number, [number, number]> = {
	7: [22, 28],
	6: [18, 22],
	5: [14, 17],
	4: [9, 12],
	3: [5, 7],
	2: [3, 4],
	1: [2, 3],
};

// California city names for special India connections
const CALIFORNIA_CITIES = ["San Francisco", "Los Angeles"];
const INDIA_CITIES = ["Mumbai", "Bangalore", "Delhi", "Hyderabad", "Chennai", "Pune", "Kolkata"];

function generateStaticArcs(): Arc[] {
	const arcs: Arc[] = [];
	const usedConnections = new Set<string>();

	// Helper to add an arc
	const addArc = (cityIdx: number, endIdx: number) => {
		const key = cityIdx < endIdx ? `${cityIdx}-${endIdx}` : `${endIdx}-${cityIdx}`;
		if (usedConnections.has(key)) return;
		usedConnections.add(key);

		const startCity = CITIES[cityIdx];
		const endCity = CITIES[endIdx];

		const latDiff = Math.abs(startCity.lat - endCity.lat);
		const lngDiff = Math.abs(startCity.lng - endCity.lng);
		const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

		// Arc heights: varied (0.04 to 0.3 range)
		const baseAlt = 0.04 + Math.random() * 0.05; // 0.04-0.09 base
		const distanceFactor = Math.min(distance / 300, 1) * 0.14;
		const randomVariation = Math.random() * 0.07;
		const arcAlt = baseAlt + distanceFactor + randomVariation;

		const arcWeight = Math.max(startCity.weight, endCity.weight);
		const baseOpacity = randomBaseOpacity(arcWeight);

		arcs.push({
			id: `${cityIdx}-${endIdx}`,
			startLat: startCity.lat,
			startLng: startCity.lng,
			endLat: endCity.lat,
			endLng: endCity.lng,
			arcAlt,
			baseOpacity,
			opacity: baseOpacity,
			weight: arcWeight,
		});
	};

	// First, ensure California-India connections
	const caliIndices = CITIES.map((c, i) => CALIFORNIA_CITIES.includes(c.name) ? i : -1).filter(i => i >= 0);
	const indiaIndices = CITIES.map((c, i) => INDIA_CITIES.includes(c.name) ? i : -1).filter(i => i >= 0);

	caliIndices.forEach((caliIdx) => {
		indiaIndices.forEach((indiaIdx) => {
			addArc(caliIdx, indiaIdx);
		});
	});

	CITIES.forEach((city, cityIdx) => {
		const [min, max] = CONNECTION_COUNTS[city.weight] || [2, 3];
		const numConnections = min + Math.floor(Math.random() * (max - min + 1));

		// Get random destination cities
		const availableCities = CITIES.map((_, i) => i).filter((i) => i !== cityIdx);
		const shuffled = shuffleArray(availableCities);
		const destinations = shuffled.slice(0, numConnections);

		destinations.forEach((endIdx) => {
			addArc(cityIdx, endIdx);
		});
	});

	return arcs;
}

// Calculate sun position based on UTC time
function getSunPosition(): { x: number; y: number; z: number } {
	const now = new Date();
	const utcHours = now.getUTCHours();
	const utcMinutes = now.getUTCMinutes();

	const sunLongitude = (12 - utcHours - utcMinutes / 60) * 15;
	const lonRad = (sunLongitude * Math.PI) / 180;
	const latRad = 0;
	const distance = 200;
	const x = distance * Math.cos(latRad) * Math.sin(lonRad);
	const y = distance * Math.sin(latRad);
	const z = distance * Math.cos(latRad) * Math.cos(lonRad);

	return { x, y: y + 30, z };
}

interface EarthProps {
	width?: number;
	height?: number;
	enablePointerInteraction?: boolean;
	atmosphereAltitude?: number;
	noBackground?: boolean;
	globeOffset?: [number, number];
	initialLat?: number;
	initialLng?: number;
	altitude?: number;
	autoRotateSpeed?: number;
	isPaused?: boolean;
}

export default function Earth({
	width,
	height,
	enablePointerInteraction = true,
	atmosphereAltitude = 0.08,
	noBackground = true,
	globeOffset = [0, 0],
	initialLat = 39,
	initialLng = -140,
	altitude = 1.8,
	autoRotateSpeed = -0.21,
	isPaused = false,
}: EarthProps) {
	const globeEl = useRef<GlobeMethods | undefined>(undefined);
	const controlsRef = useRef<GlobeControls | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const pulseAnimationRef = useRef<number | null>(null);
	const pulsingArcsRef = useRef<Map<string, PulseState>>(new Map());
	const staggerTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
	const [mounted, setMounted] = useState(false);
	const [isClient, setIsClient] = useState(false);

	// Static arcs stored in ref - never changes after generation
	const staticArcsRef = useRef<Arc[]>([]);
	// Pulsing opacities - only stores opacities for actively pulsing arcs
	const [pulsingOpacities, setPulsingOpacities] = useState<Map<string, number>>(new Map());

	// Generate static arcs once on mount
	useEffect(() => {
		if (!isClient) return;
		staticArcsRef.current = generateStaticArcs();
		// Force initial render
		setPulsingOpacities(new Map());
	}, [isClient]);

	// Memoized arcs with current opacities for rendering
	// Sort by opacity so live (high-opacity) arcs render last = on top
	const arcsForRender = useMemo(() => {
		return staticArcsRef.current.map(arc => {
			const pulsingOpacity = pulsingOpacities.get(arc.id);
			if (pulsingOpacity !== undefined) {
				return { ...arc, opacity: pulsingOpacity };
			}
			return arc;
		}).sort((a, b) => a.opacity - b.opacity);
	}, [pulsingOpacities]);

	// Pulse trigger - randomly select arcs to pulse, favoring higher-weight connections
	useEffect(() => {
		if (!isClient || staticArcsRef.current.length === 0 || isPaused) return;

		// Weighted random selection - higher weight arcs are more likely to pulse
		const selectWeightedArc = (available: Arc[]): Arc | null => {
			if (available.length === 0) return null;
			// Weight^2 to make hot spots significantly more active
			const totalWeight = available.reduce((sum, arc) => sum + arc.weight * arc.weight, 0);
			let random = Math.random() * totalWeight;
			for (const arc of available) {
				random -= arc.weight * arc.weight;
				if (random <= 0) return arc;
			}
			return available[available.length - 1];
		};

		const triggerPulse = () => {
			const numToPulse = 1 + Math.floor(Math.random() * 2); // 1-2 arcs
			let availableArcs = staticArcsRef.current.filter(
				(arc) => !pulsingArcsRef.current.has(arc.id)
			);

			if (availableArcs.length === 0) return;

			const toPulse: Arc[] = [];
			for (let i = 0; i < numToPulse && availableArcs.length > 0; i++) {
				const selected = selectWeightedArc(availableArcs);
				if (selected) {
					toPulse.push(selected);
					availableArcs = availableArcs.filter((a) => a.id !== selected.id);
				}
			}

			// Stagger arc pulses with randomized delays
			toPulse.forEach((arc, index) => {
				const staggerDelay = index * (80 + Math.random() * 200); // 80-280ms between each
				const timeoutId = setTimeout(() => {
					staggerTimeoutsRef.current.delete(timeoutId);
					pulsingArcsRef.current.set(arc.id, {
						startTime: performance.now(),
						phase: "up",
					});
				}, staggerDelay);
				staggerTimeoutsRef.current.add(timeoutId);
			});
		};

		const scheduleNext = () => {
			// Wider random range for organic feel — occasional bursts with quiet gaps
			const delay = 300 + Math.random() * 900; // 300-1200ms, avg ~750ms
			return setTimeout(() => {
				triggerPulse();
				timeoutRef = scheduleNext();
			}, delay);
		};
		let timeoutRef = scheduleNext();

		return () => {
			clearTimeout(timeoutRef);
			// Clear all stagger timeouts
			staggerTimeoutsRef.current.forEach(id => clearTimeout(id));
			staggerTimeoutsRef.current.clear();
		};
	}, [isClient, isPaused]);

	// Animation loop for smooth opacity interpolation
	useEffect(() => {
		if (!isClient || isPaused) return;

		const animate = () => {
			// Early return if no arcs are pulsing - skip state update entirely
			if (pulsingArcsRef.current.size === 0) {
				pulseAnimationRef.current = requestAnimationFrame(animate);
				return;
			}

			const now = performance.now();
			const newOpacities = new Map<string, number>();
			let hasChanges = false;

			// Only process arcs that are currently pulsing
			const entries = Array.from(pulsingArcsRef.current.entries());
			for (const [arcId, pulseState] of entries) {
				const arc = staticArcsRef.current.find(a => a.id === arcId);
				if (!arc) continue;

				const elapsed = now - pulseState.startTime;
				let newOpacity: number;

				if (pulseState.phase === "up") {
					const progress = Math.min(elapsed / PULSE_UP_DURATION, 1);
					newOpacity =
						arc.baseOpacity +
						(PEAK_OPACITY - arc.baseOpacity) * easeOutQuart(progress);

					if (progress >= 1) {
						// Transition to down phase
						pulsingArcsRef.current.set(arcId, {
							startTime: now,
							phase: "down",
						});
					}
				} else {
					const progress = Math.min(elapsed / PULSE_DOWN_DURATION, 1);
					newOpacity =
						PEAK_OPACITY -
						(PEAK_OPACITY - arc.baseOpacity) * easeOutExpo(progress);

					if (progress >= 1) {
						pulsingArcsRef.current.delete(arcId);
						// Don't add to newOpacities - arc returns to base opacity
						hasChanges = true;
						continue;
					}
				}

				newOpacities.set(arcId, newOpacity);
				hasChanges = true;
			}

			if (hasChanges) {
				setPulsingOpacities(newOpacities);
			}

			pulseAnimationRef.current = requestAnimationFrame(animate);
		};

		pulseAnimationRef.current = requestAnimationFrame(animate);

		return () => {
			if (pulseAnimationRef.current) {
				cancelAnimationFrame(pulseAnimationRef.current);
			}
		};
	}, [isClient, isPaused]);

	useEffect(() => {
		setIsClient(true);
		setMounted(true);
	}, []);

	useEffect(() => {
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, []);

	if (!isClient || !mounted) {
		return <div className="relative h-full w-full" />;
	}

	return (
		<div
			className={`relative h-full w-full ${enablePointerInteraction ? "cursor-grab active:cursor-grabbing" : ""}`}
		>
			<Suspense fallback={null}>
				<Globe
					ref={globeEl}
					globeOffset={globeOffset}
					width={width}
					backgroundColor="#0a0a0a"
					height={height}
					globeImageUrl={earthDayImage}
					bumpImageUrl={earthTopologyImage}
					backgroundImageUrl={noBackground ? undefined : nightSkyImage}
					atmosphereColor="#4a90c2"
					atmosphereAltitude={atmosphereAltitude}
					enablePointerInteraction={enablePointerInteraction}
					// White arcs with per-arc opacity for pulse effect
					arcsData={arcsForRender}
					arcStartLat="startLat"
					arcStartLng="startLng"
					arcEndLat="endLat"
					arcEndLng="endLng"
					arcColor={(d: object) => getArcColor((d as Arc).opacity)}
					arcAltitude={(d: object) => (d as Arc).arcAlt}
					arcStroke={0.35}
					arcsTransitionDuration={0}
					onGlobeReady={() => {
						if (!globeEl.current) return;

						const globe = globeEl.current;

						globe.pointOfView({
							lat: initialLat,
							lng: initialLng,
							altitude: altitude,
						});

						const controls = globe.controls?.() as GlobeControls | undefined;
						if (controls) {
							controls.autoRotate = true;
							controls.autoRotateSpeed = autoRotateSpeed;
							controls.enableZoom = false;
							controls.enablePan = false;
							controls.enableRotate = enablePointerInteraction;

							controlsRef.current = controls;

							const animate = () => {
								controls.update();
								animationFrameRef.current = requestAnimationFrame(animate);
							};
							animate();
						}

						const scene = globe.scene?.() as SceneLike | undefined;
						const THREE = (window as Window & { THREE?: ThreeLike }).THREE;

						if (!scene || !THREE) return;

						const lightsToRemove = scene.children.filter(
							(child) => child.isLight
						);
						lightsToRemove.forEach((light) => {
							scene.remove(light);
						});

						const sunPos = getSunPosition();

						const sunLight = new THREE.DirectionalLight(0xffffff, 2.8);
						sunLight.position.set(sunPos.x, sunPos.y, sunPos.z);
						scene.add(sunLight);

						const fillLight = new THREE.DirectionalLight(0x223344, 0.08);
						fillLight.position.set(-sunPos.x, -sunPos.y, -sunPos.z);
						scene.add(fillLight);

						const ambientLight = new THREE.AmbientLight(0x112233, 0.15);
						scene.add(ambientLight);

						const renderer = globe.renderer?.() as GlobeRenderer | undefined;
						if (renderer) {
							renderer.toneMapping = THREE.ACESFilmicToneMapping;
							renderer.toneMappingExposure = 1.1;
							renderer.outputColorSpace = THREE.SRGBColorSpace;
							// Three.js color management converts sRGB→linear, but gl.clearColor
							// uses linear values in the sRGB framebuffer. #0a0a0a becomes ~#010101.
							// #383838 → linear ≈ 0.0392 → displays as #0a0a0a (matches page bg).
							renderer.setClearColor(0x383838, 1);
						}
					}}
				/>
			</Suspense>
		</div>
	);
}
